import { Fragment, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { Hit } from 'instantsearch.js';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import AddToCart from '@/components/customercenter-ui/svgs/addToCart';
import NoImage from '@/components/customercenter-ui/svgs/noImage';
import { renderProductUrl } from '@/components/customercenter-ui/utils/formatUtils';
import StarFill from '@/customercenter-ui/icons/startFill';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { filterOnSaleItem } from '@/helpers/utils/AlgoliaInsights';
import { clearanceTextClass } from '@/helpers/utils/brandButton';
import {
  buildPlProductBadgingUtilInput,
  computePlOrderedRuleBadges,
  getPlBadgeComputationSignature,
  isPlBadgeConfigurationsEnabled,
} from '@/helpers/utils/plpProductBadging';
import type { BadgeConfiguration, ProductBadgingEntry } from '@/helpers/utils/productBadging';
import { formatCurrency, getSelectedDivision, getSelectedSite, TranslateText } from '@/helpers/utils/siteUtils';
import { useProductListingContext } from './context/productListingContext';
import ProductRuleBadges from './ProductRuleBadges';
import { SwatchThumbnail } from './swatch';
import { DEFAULT_SITE_LANG } from '../../../../../../constants/constant';
import ContentStackImage from '../../../../common/contentStackImage/contentStackImage';
import Tags from '../../../tags';
import { Button } from '../../../ui/button';
import { PageTexts } from '../i18n';
import { AlgoliaProductTypes, ProductDetailsSectionProps, Swatch } from '../types';

function trimSpaceFromFilterValue(filter: string): string {
  if (!filter || !filter.trim()) return '';
  return filter
    .trim()
    .replace(/\s{2,}/g, ' ')
    .split(' ')
    .join('-');
}

function coercePriceLabelForTags(label: unknown): string | undefined {
  if (label == null) return undefined;
  if (Array.isArray(label)) {
    const parts = label
      .map(String)
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.length ? parts.join(', ') : undefined;
  }
  if (typeof label === 'string') {
    const t = label.trim();
    return t.length ? t : undefined;
  }
  return undefined;
}

function getSelectedSizesAndWidthsFromFilters(filters: any[] | undefined): { sizes: string[]; widths: string[] } {
  if (!filters || !Array.isArray(filters)) {
    return { sizes: [], widths: [] };
  }
  const sizeFilter = filters.find((f: any) => f.attribute === 'refinementSize');
  const widthFilter = filters.find((f: any) => f.attribute === 'footwearWidth');
  const sizes = (sizeFilter?.refinements ?? []).map((r: any) => r?.label ?? r?.value).filter(Boolean);
  const widths = (widthFilter?.refinements ?? []).map((r: any) => r?.label ?? r?.value).filter(Boolean);
  return { sizes, widths };
}

function normalizeVariantFacetValue(value: string | undefined | null): string {
  if (value == null || String(value).trim() === '') return '';
  return trimSpaceFromFilterValue(String(value).trim());
}

function variantCellsIncludeSelections(cells: string[] | undefined, selections: string[]): boolean {
  if (!selections.length) return true;
  if (!cells?.length) return false;
  const normalizedCells = cells.map((c) => normalizeVariantFacetValue(c));
  const rawCells = cells.map((c) => String(c ?? '').trim());
  return selections.some((sel) => {
    const rs = String(sel ?? '').trim();
    if (!rs) return false;
    const ns = normalizeVariantFacetValue(sel);
    return (
      normalizedCells.includes(ns) ||
      rawCells.includes(rs) ||
      rawCells.some((c) => c.toLowerCase() === rs.toLowerCase())
    );
  });
}

/**
 * Resolves `variantDetails` for facet-driven price tags / badge overrides.
 * - Selected swatch: use that swatch’s `variantDetails` when present.
 * - No swatch (`null`): master is selected — use `hit.variantDetails` when present (before scanning other swatches).
 * - Facets on, no master/swatch `variantDetails`: fall back to first swatch whose `variantDetails` matches facets.
 */
function resolveVariantDetailsForFacetPriceLabels(
  hit: any,
  selectedSwatch: Swatch | null,
  filters: any[] | undefined,
): any[] | undefined {
  const { sizes, widths } = getSelectedSizesAndWidthsFromFilters(filters);
  const facetActive = sizes.length > 0 || widths.length > 0;

  if (selectedSwatch?.variantDetails?.length) {
    return selectedSwatch.variantDetails;
  }
  if (selectedSwatch == null) {
    const masterVariantDetails = hit?.variantDetails;
    if (Array.isArray(masterVariantDetails) && masterVariantDetails.length) {
      return masterVariantDetails;
    }
  }
  if (!facetActive) {
    return undefined;
  }
  const swatches = hit?.swatches;
  if (Array.isArray(swatches)) {
    for (const sw of swatches) {
      const vd = sw?.variantDetails;
      if (vd?.length && findVariantDetailForFacets(vd, sizes, widths)) {
        return vd;
      }
    }
  }
  const hitVd = hit?.variantDetails;
  return Array.isArray(hitVd) && hitVd.length ? hitVd : undefined;
}

function findVariantDetailForFacets(
  variantDetails: any[] | undefined,
  selectedSizes: string[],
  selectedWidths: string[],
): any | null {
  if (!variantDetails?.length) return null;
  if (!selectedSizes.length && !selectedWidths.length) return null;
  return (
    variantDetails.find(
      (v: any) =>
        variantCellsIncludeSelections(v?.size, selectedSizes) &&
        variantCellsIncludeSelections(v?.width, selectedWidths),
    ) ?? null
  );
}

function overrideTagLabelFromVariantDetails(
  variantDetails: any[] | undefined,
  filters: any[] | undefined,
): string | undefined {
  const { sizes, widths } = getSelectedSizesAndWidthsFromFilters(filters);
  const variant = findVariantDetailForFacets(variantDetails, sizes, widths);
  const priceLabels = variant?.priceLabels;
  if (!Array.isArray(priceLabels) || priceLabels.length === 0) return undefined;
  const joined = priceLabels
    .map(String)
    .map((s) => s.trim())
    .filter(Boolean)
    .join(', ');
  return joined.length ? joined : undefined;
}

function overrideBadgeLabelsFromVariantDetails(
  variantDetails: any[] | undefined,
  filters: any[] | undefined,
): string[] | undefined {
  const { sizes, widths } = getSelectedSizesAndWidthsFromFilters(filters);
  const variant = findVariantDetailForFacets(variantDetails, sizes, widths);
  const labels = variant?.overrideBadgeLabels;
  if (!Array.isArray(labels) || labels.length === 0) return undefined;
  const cleaned = labels
    .map(String)
    .map((s) => s.trim())
    .filter(Boolean);
  return cleaned.length ? cleaned : undefined;
}

const isCmsPlRuleBadgeRenderable = (entry: BadgeConfiguration | null | undefined): boolean => {
  if (!entry) return false;
  const hasLabel = entry.badgeName != null && String(entry.badgeName).trim().length > 0;
  const hasImage = Boolean(entry.backgroundImage?.trim());
  if (!hasLabel && !hasImage) return false;
  const enabled = entry.Enabled as unknown;
  if (enabled === false || enabled === 0) return false;
  if (typeof enabled === 'string') {
    const normalized = enabled.trim().toLowerCase();
    if (normalized === 'false' || normalized === '0') return false;
  }
  return true;
};

type HitProps = {
  hit: Hit<AlgoliaProductTypes>;
};
export const ProductItem = ({
  hit,
  rating,
  productBadgingEntries,
  filters,
}: {
  hit: Hit<AlgoliaProductTypes>;
  rating?: any;
  productBadgingEntries?: ProductBadgingEntry[] | null;
  filters?: any[];
}) => {
  const [selectedSwatch, setSelectedSwatch] = useState<Swatch | null>(null);
  const [productUrl, setProductURL] = useState<string>(renderProductUrl(`/product/${hit?.pdpSeo}/${hit?.key}`));
  const handleSwatchSelect = (swatch: Swatch) => {
    setSelectedSwatch(swatch);
    setProductURL(renderProductUrl(`/product/${swatch?.pdpSeo}/${swatch?.key}`));
    updatePrices(swatch);
  };

  const handleMasterProductSelect = () => {
    setSelectedSwatch(null);
    setProductURL(renderProductUrl(`/product/${hit?.pdpSeo}/${hit?.key}`));
    resetPrices();
  };

  const updatePrices = (swatch: Swatch | null) => {
    if (!hit.defaultWasPrice || !hit.defaultPricePerMonth) {
      hit.defaultWasPrice = hit.wasPrice;
      hit.defaultPricePerMonth = hit.pricePerMonth;
    }

    if (swatch) {
      hit.wasPrice = swatch.wasPrice;
      hit.pricePerMonth = swatch.pricePerMonth;
    }
  };

  const resetPrices = () => {
    hit.wasPrice = hit.defaultWasPrice as string | undefined;
    const defaults = hit as Hit<AlgoliaProductTypes> & { defaultPricePerMonth?: string };
    if (defaults.defaultPricePerMonth != null) {
      hit.pricePerMonth = defaults.defaultPricePerMonth;
    }
  };

  return (
    <main className="mb-6 flex gap-4 rounded-md border-[1px] border-foggyGray px-4 py-6 shadow-lg">
      <div className="w-56">
        {' '}
        <ProductImageSection
          hit={hit}
          selectedSwatch={selectedSwatch}
          productUrl={productUrl}
          productBadgingEntries={productBadgingEntries}
          filters={filters}
        />{' '}
        <section className="order-2 lg:order-1">
          <SwatchesSection
            hit={hit}
            selectedSwatch={selectedSwatch}
            onSwatchSelect={handleSwatchSelect}
            onMasterProductSelect={handleMasterProductSelect}
          />
        </section>
      </div>
      <ProductDetailsSection
        hit={hit}
        selectedSwatch={selectedSwatch}
        onSwatchSelect={handleSwatchSelect}
        onMasterProductSelect={handleMasterProductSelect}
        productUrl={productUrl}
        rating={rating}
        filters={filters}
      />
    </main>
  );
};
export const ProductImageSection = ({
  hit,
  selectedSwatch,
  productUrl,
  productBadgingEntries,
  filters,
}: {
  hit: HitProps['hit'];
  selectedSwatch: Swatch | null;
  productUrl: any;
  productBadgingEntries?: ProductBadgingEntry[] | null;
  filters?: any[];
}) => {
  const projectSettings: ProjectSettings = useProjSettings();
  const selectedDivision = getSelectedDivision() ?? 'ZB';
  const siteId = getSelectedSite() ?? 'ZBSite';

  const badgeSettings = projectSettings?.badgeConfigurations.find(
    (badge: any) => badge?.['SITE_ID'] === selectedDivision,
  )?.BADGES_LIST?.[0];

  const cmsPlBadgingOn = isPlBadgeConfigurationsEnabled(badgeSettings?.ENABLE_BADGE_CONFIGURATIONS);
  const badgeConfigurationsJson = useMemo(
    () => JSON.stringify(badgeSettings?.BADGE_CONFIGURATIONS ?? null),
    [badgeSettings?.BADGE_CONFIGURATIONS],
  );
  const maxBadgesPlpResolved = useMemo(() => {
    const raw = badgeSettings?.MAX_BADGES_PLP;
    const n = typeof raw === 'string' ? parseInt(String(raw).trim(), 10) : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [projectSettings?.maxBadgesPlp]);

  const facetBadgingFromFacets = useMemo(() => {
    const variantDetailsForBadges = resolveVariantDetailsForFacetPriceLabels(hit, selectedSwatch, filters);
    const facetVariantOverrideBadgeLabels = overrideBadgeLabelsFromVariantDetails(variantDetailsForBadges, filters);
    const { sizes, widths } = getSelectedSizesAndWidthsFromFilters(filters);
    const plpFacetBadgeSignature = `${sizes.join('\x1e')}||${widths.join('\x1e')}||${
      facetVariantOverrideBadgeLabels?.join('\x1e') ?? ''
    }`;
    return { facetVariantOverrideBadgeLabels, plpFacetBadgeSignature };
  }, [hit, selectedSwatch, filters]);

  const plpBadgeComputationSig = useMemo(
    () =>
      getPlBadgeComputationSignature({
        hit,
        selectedSwatch,
        productBadgingEntries,
        badgeConfigurationsJson,
        maxBadgesPlpKey: String(maxBadgesPlpResolved ?? ''),
        plpFacetBadgeSignature: facetBadgingFromFacets.plpFacetBadgeSignature,
      }),
    [
      hit,
      selectedSwatch,
      productBadgingEntries,
      badgeConfigurationsJson,
      maxBadgesPlpResolved,
      facetBadgingFromFacets.plpFacetBadgeSignature,
    ],
  );

  const [plRuleBadges, setPlRuleBadges] = useState<BadgeConfiguration[] | undefined>(undefined);
  const [plTopRuleReady, setPlTopRuleReady] = useState(false);

  useEffect(() => {
    if (!cmsPlBadgingOn || productBadgingEntries === undefined) {
      setPlRuleBadges(undefined);
      setPlTopRuleReady(false);
      return;
    }
    if (productBadgingEntries === null) {
      setPlRuleBadges(undefined);
      setPlTopRuleReady(false);
      return;
    }

    setPlTopRuleReady(false);
    setPlRuleBadges(undefined);

    const input = buildPlProductBadgingUtilInput({
      hit,
      selectedSwatch,
      productBadgingEntries,
      badgeConfigurations: badgeSettings?.BADGE_CONFIGURATIONS,
      maxVisibleBadges: maxBadgesPlpResolved,
      facetVariantOverrideBadgeLabels: facetBadgingFromFacets.facetVariantOverrideBadgeLabels,
    });

    setPlRuleBadges(computePlOrderedRuleBadges(input));
    setPlTopRuleReady(true);
  }, [
    cmsPlBadgingOn,
    productBadgingEntries,
    plpBadgeComputationSig,
    badgeSettings?.BADGE_CONFIGURATIONS,
    maxBadgesPlpResolved,
  ]);

  const plVisibleRuleBadges = useMemo(
    () => (Array.isArray(plRuleBadges) ? plRuleBadges.filter((entry) => isCmsPlRuleBadgeRenderable(entry)) : []),
    [plRuleBadges],
  );

  const showCmsPlRulePill =
    cmsPlBadgingOn &&
    productBadgingEntries !== undefined &&
    Array.isArray(productBadgingEntries) &&
    plTopRuleReady &&
    Array.isArray(plRuleBadges) &&
    plRuleBadges.some(isCmsPlRuleBadgeRenderable);

  return (
    <section className="">
      <section className="relative w-[200px]">
        {renderProductImage(hit, selectedSwatch, true, productUrl)}
        {hit?.discount && (
          <section className="absolute left-1 top-1 z-[1]">
            <section
              className={`min-h-[30px] rounded-none ${clearanceTextClass(
                siteId,
              )} px-3 py-1 text-center text-sm font-bold leading-7 text-white`}
            >
              {hit?.discount}
            </section>
          </section>
        )}
        {showCmsPlRulePill && plVisibleRuleBadges.length ? (
          <div className="pointer-events-none absolute inset-0 z-[12]">
            <ProductRuleBadges badges={plVisibleRuleBadges} layout="pdpCorner" />
          </div>
        ) : null}
      </section>
    </section>
  );
};

export const ProductDetailsSection = ({
  hit,
  selectedSwatch,
  show,
  productUrl,
  rating,
  filters,
}: ProductDetailsSectionProps) => {
  return (
    <section className="md:my-3">
      <section className={show ? '' : 'flex flex-col'}>
        <section className="order-1 lg:order-2">
          <Link href={productUrl}>
            <p className="min-h-10 mt-3 line-clamp-2 break-words text-[15px] font-medium leading-[21px] text-black ">
              {hit?.displayName}
            </p>
          </Link>
        </section>
      </section>
      <PriceSection
        hit={hit}
        selectedSwatch={selectedSwatch}
        productUrl={productUrl}
        rating={rating}
        filters={filters}
      />
    </section>
  );
};

const PriceSection = ({
  hit,
  selectedSwatch,
  productUrl,
  rating,
  filters,
}: {
  hit: HitProps['hit'];
  selectedSwatch: Swatch | null;
  productUrl: string;
  rating: any;
  filters?: any[];
}) => {
  const router = useRouter();
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const { setLoading } = useProductListingContext();
  const projectSettings: ProjectSettings = useProjSettings();
  const siteId = getSelectedSite() ?? 'ZBSite';

  let activePriceTypes: string[] = [];
  if (hit?.clearance) {
    activePriceTypes = [];
    activePriceTypes.push('CP');
  }
  if (hit?.onPromoPrice) {
    activePriceTypes = [];
    activePriceTypes.push('PP');
  }
  if (selectedSwatch && selectedSwatch?.clearance) {
    activePriceTypes = [];
    activePriceTypes.push('CP');
  }
  if (selectedSwatch && selectedSwatch?.onPromoPrice) {
    activePriceTypes = [];
    activePriceTypes.push('PP');
  }

  const variantDetailsForLabels = resolveVariantDetailsForFacetPriceLabels(hit, selectedSwatch, filters);
  const tagLabelFromVariant = overrideTagLabelFromVariantDetails(variantDetailsForLabels, filters);
  const tagLabelFallback =
    coercePriceLabelForTags(selectedSwatch?.priceLabel) ?? coercePriceLabelForTags(hit?.priceLabel);
  const { sizes: selectedSizesForTags, widths: selectedWidthsForTags } = getSelectedSizesAndWidthsFromFilters(filters);
  const sizeAndWidthFacetsSelected = selectedSizesForTags.length > 0 && selectedWidthsForTags.length > 0;
  const hasFacetTagLabelFromVariant = tagLabelFromVariant != null && String(tagLabelFromVariant).trim().length > 0;
  const resolvedOverrideTagLabel: string | string[] | undefined = hasFacetTagLabelFromVariant
    ? tagLabelFromVariant
    : sizeAndWidthFacetsSelected
    ? []
    : tagLabelFallback;

  return (
    <div className="min-h-[50px]">
      <div className="mt-3 min-h-[50px]">
        <Tags activePriceType={activePriceTypes} overrideTagLabel={resolvedOverrideTagLabel} />
        {hit?.minListPrice ? (
          <p className="break-words text-[16px] font-normal leading-[19.6px] text-[#666666]">
            {(filterOnSaleItem(hit, projectSettings) || hit?.onDiscount || hit?.onNewListPrice) && hit?.minSalePrice ? (
              <>
                <strong
                  className={`break-words pr-2 text-base font-bold leading-[20.8px] ${clearanceTextClass(siteId)}`}
                >
                  {hit?.maxSalePrice && hit?.minSalePrice && hit?.maxSalePrice > hit?.minSalePrice ? (
                    <>
                      {formatCurrency(hit?.minSalePrice?.toString())} - {formatCurrency(hit?.maxSalePrice?.toString())}
                    </>
                  ) : (
                    <>{formatCurrency(hit?.minSalePrice?.toString())}</>
                  )}
                </strong>
                <em className="break-words not-italic line-through">
                  {hit?.maxListPrice && hit?.maxListPrice > hit?.minListPrice ? (
                    <>
                      {formatCurrency(hit?.minListPrice?.toString())} - {formatCurrency(hit?.maxListPrice?.toString())}
                    </>
                  ) : (
                    <strong
                      className={`break-words pr-2 text-lg font-bold leading-[23.4px] ${clearanceTextClass(siteId)}`}
                    >
                      {formatCurrency(hit?.minListPrice?.toString())}
                    </strong>
                  )}
                </em>
              </>
            ) : (
              <span>
                {hit?.maxListPrice && hit?.maxListPrice > hit?.minListPrice ? (
                  <>
                    {formatCurrency(hit?.minListPrice?.toString())} - {formatCurrency(hit?.maxListPrice?.toString())}
                  </>
                ) : (
                  <strong
                    className={`break-words pr-2 text-lg font-bold leading-[23.4px] ${clearanceTextClass(siteId)}`}
                  >
                    {formatCurrency(hit?.minListPrice?.toString())}
                  </strong>
                )}
              </span>
            )}
          </p>
        ) : (
          <></>
        )}

        {hit?.pricePerMonth && (
          <section>
            <strong className="inline-block text-lg font-bold leading-[23.4px] text-black">
              {formatCurrency(hit?.pricePerMonth)}/month
            </strong>
          </section>
        )}
      </div>

      <section className="text-md flex items-center gap-2 text-gray-400">
        <span className={`${hit?.ratingCount ? 'border-r border-[#656668] pr-3' : ''}`}>Item #: {hit?.abwStyle}</span>
        {hit?.ratingCount && (
          <section className="flex items-center gap-1">
            <StarFill value={rating} count={5} productId={hit?.objectID} /> ({hit?.ratingCount ?? 0})
          </section>
        )}
      </section>

      <Button
        className="mt-4 flex h-11 w-[150px] items-center gap-2 rounded-md border border-[#B6B6B8] px-4 shadow-sm"
        type="button"
        onClick={() => {
          router.push(productUrl);
          setLoading(true);
        }}
      >
        <AddToCart className="h-4 w-4" />
        {Texts.addToCart}
      </Button>
    </div>
  );
};

const SwatchesSection = ({
  hit,
  selectedSwatch,
  onSwatchSelect,
  onMasterProductSelect,
}: {
  hit: HitProps['hit'];
  selectedSwatch: Swatch | null;
  onSwatchSelect: (swatch: Swatch) => void;
  onMasterProductSelect: () => void;
}) => {
  return (
    <div className="relative mt-2 flex w-max gap-2">
      <MasterProductSwatch hit={hit} isSelected={!selectedSwatch} onMasterProductSelect={onMasterProductSelect} />
      {hit?.swatches &&
        hit.swatches
          .slice(0, 3)
          .map((swatch) => (
            <SwatchThumbnail
              key={swatch.id}
              swatch={swatch}
              isSelected={selectedSwatch === swatch}
              onSwatchSelect={onSwatchSelect}
            />
          ))}
      {hit?.swatches && hit.swatches.length > 4 && (
        <div className="flex items-center">
          <Listbox>
            <div className="">
              <ListboxButton className="relative w-full cursor-pointer bg-white  pr-5  text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                <span className="block truncate text-sm leading-[19.6px] text-black">+{hit.swatches.length - 3}</span>
              </ListboxButton>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions className="absolute right-0 top-10 z-10 mt-1 w-[200px]  rounded-md  bg-white text-base ring-1 ring-black/5 focus:outline-none sm:text-sm md:right-0 lg:-right-4">
                  <section className="relative rounded-md border border-foggyGray  bg-white py-3 shadow-md">
                    <div className="absolute right-4 top-0 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 border-l border-t border-foggyGray bg-white"></div>
                    <ul className="customtablescrollbar grid max-h-[277px] gap-3 overflow-y-auto px-2">
                      <li className="mt-1 flex gap-2" title={hit?.color} key={`master swatch`}>
                        <MasterProductSwatch
                          hit={hit}
                          isSelected={!selectedSwatch}
                          onMasterProductSelect={onMasterProductSelect}
                        />

                        <button
                          type="button"
                          className={`text-left text-sm ${!selectedSwatch ? 'font-bold' : ''}`}
                          onClick={() => onMasterProductSelect()}
                        >
                          {hit?.color}
                        </button>
                      </li>
                      {hit?.swatches &&
                        hit.swatches.map((swatch, index) => (
                          <li className="flex gap-2" key={`swatch_${index}`} title={swatch?.color}>
                            <SwatchThumbnail
                              key={`${swatch.id}-${index}`}
                              swatch={swatch}
                              isSelected={selectedSwatch === swatch}
                              onSwatchSelect={onSwatchSelect}
                            />
                            <button
                              type="button"
                              className={`text-left text-sm ${selectedSwatch === swatch ? 'font-bold' : ''}`}
                              onClick={() => onSwatchSelect(swatch)}
                            >
                              {swatch.color}
                            </button>
                          </li>
                        ))}
                    </ul>
                  </section>
                </ListboxOptions>
              </Transition>
            </div>
          </Listbox>
        </div>
      )}
    </div>
  );
};

const MasterProductSwatch = ({
  hit,
  isSelected,
  onMasterProductSelect,
}: {
  hit: HitProps['hit'];
  isSelected: boolean;
  onMasterProductSelect: () => void;
}) => {
  return (
    <div
      aria-hidden="true"
      className={`h-9 w-9 cursor-pointer overflow-hidden rounded-full border ${
        isSelected ? 'border-2 border-black' : 'border-foggyGray'
      }`}
      style={{ backgroundColor: 'transparent' }}
      onClick={onMasterProductSelect}
    >
      {hit?.mainImageURL ? (
        <ContentStackImage
          className={'rounded-full pt-[2px] '}
          src={hit?.mainImageURL}
          imageWidth={32}
          imageHeight={32}
          alt={hit?.thumbnailTitle as string}
          loading="eager"
        />
      ) : (
        <NoImage className={'rounded-full'} width={32} height={32} />
      )}
    </div>
  );
};

const renderProductImage = (
  hit: HitProps['hit'],
  selectedSwatch: Swatch | null,
  show: boolean | undefined,
  productUrl: any,
) => {
  if (selectedSwatch && selectedSwatch.mainImageURL) {
    return (
      <Link href={productUrl}>
        <ContentStackImage
          className={show ? 'w-[200px]' : ''}
          src={selectedSwatch?.mainImageURL}
          imageWidth={200}
          imageHeight={200}
          fullWidth={true}
          alt={selectedSwatch?.key as string}
          loading="eager"
        />
      </Link>
    );
  } else if (selectedSwatch && !selectedSwatch.mainImageURL) {
    return (
      <Link href={productUrl}>
        <NoImage className={show ? 'h-full w-[200px]' : ''} />
      </Link>
    );
  } else if (hit && hit.mainImageURL) {
    return (
      <Link href={productUrl}>
        <ContentStackImage
          className={show ? 'w-[200px]' : ''}
          src={hit?.mainImageURL}
          imageWidth={200}
          imageHeight={200}
          fullWidth={true}
          alt={selectedSwatch?.key as string}
          loading="eager"
        />
      </Link>
    );
  } else {
    return (
      <Link href={productUrl}>
        <NoImage className={show ? 'h-full w-[200px]' : ''} />
      </Link>
    );
  }
};
